/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { classifyTier } from "@cosmos/airtable-data";
import { DirectoryCard } from "@cosmos/components";
import { useMessages } from "@epic-mw/i18n";

import {
	buildPartnerLinks,
	buildPartnerLogo,
	partnerBadgeLabel,
	partnerBadgeMessages,
	tierToTone,
} from "./buildPartnerCardProps";

import type { TrainingPartner } from "@cosmos/airtable-data";

export interface TrainingPartnerCardProps {
	data: TrainingPartner;
}

/**
 * Adapter wrapping `DirectoryCard` for a `TrainingPartners` row.
 *
 * - Logo: image if `logoUrl` is set, else a tier-driven fallback icon
 * - Badge label: derived from `partner.badgeLevel` via `classifyTier`
 *   (Bronze/Silver/Gold/Academic). Unknown values fall back to a generic
 *   "Training partner" label
 * - Tone: gold gradient reserved for the GOLD tier; Silver, Bronze, and
 *   Academic use the neutral tone — same mapping as ServicePartnerCard
 * - Links row: website + email
 * - Meta row: country + languages
 */
export function TrainingPartnerCard({ data: partner }: TrainingPartnerCardProps): React.JSX.Element {
	const messages = useMessages();
	const tier = classifyTier(partner.badgeLevel);

	const badgeLabel = partnerBadgeLabel(tier, partnerBadgeMessages(messages));

	const logo = buildPartnerLogo(partner.logoUrl, tier);

	const { links, meta } = buildPartnerLinks(partner, {
		website: messages["epic.cosmos.directory.card.website"] ?? "Website",
		email: messages["epic.cosmos.directory.card.email"] ?? "Email",
	});

	return (
		<DirectoryCard
			name={partner.name}
			badge={{ label: badgeLabel, tone: tierToTone(tier) }}
			logo={logo}
			links={links}
			meta={meta}
			primaryTags={partner.industries}
		/>
	);
}
